import { useState, useEffect, useCallback } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { callFn, fmtDate, fmtDateTime, inputStyle, labelStyle } from '../contracts/contractsApi';
import { BillingChip } from './customersUi';

const LIVE_STRIPE = ['active', 'trialing', 'past_due', 'unpaid', 'incomplete'];

const ACTION_LABELS = {
  complimentary_set: 'Made complimentary',
  complimentary_ended: 'Complimentary access ended',
  suspended: 'Suspended',
  reactivated: 'Reactivated',
  billing_link_sent: 'Billing link sent',
  billing_started: 'Billing started',
  user_disabled: 'User login disabled',
  user_enabled: 'User login enabled',
  user_role_changed: 'User role changed',
  password_setup_sent: 'Password email sent',
};

// Account management for one Ops organisation. `customer` is getOpsCustomer's
// result; `onChanged` reloads it after an action.
export default function AccountPanel({ customer: c, onChanged }) {
  const [form, setForm] = useState(null); // 'billing' | 'comp' | 'suspend' | 'reactivate'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [log, setLog] = useState(null);
  const b = c.billing;
  const liveStripe = b.kind === 'stripe' && LIVE_STRIPE.includes(b.status);
  // HQ checks with Stripe before refusing, so allow the attempt; a real live
  // subscription is still rejected server-side with an explanation.
  const canComp = b.source !== 'complimentary';

  const loadLog = useCallback(() => {
    callFn('getCustomerActions', { orgId: c.id }).then(setLog).catch(() => setLog({ actions: [], billingSetup: null }));
  }, [c.id]);
  useEffect(loadLog, [loadLog]);

  const run = async (fn, data, message) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await callFn(fn, { orgId: c.id, ...data });
      setNotice(typeof message === 'function' ? message(res) : message);
      setForm(null);
      onChanged();
      loadLog();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const adminEmail = c.profile.primaryEmail || c.users.find(u => u.role === 'org_admin')?.email || '';
  const setup = log?.billingSetup;

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, marginBottom: 6 }}>Account</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <BillingChip billing={b} />
            {b.suspended && <Pill tone="danger">Suspended</Pill>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {!liveStripe && <Button size="sm" onClick={() => setForm('billing')}>Set up billing</Button>}
          {canComp && <Button size="sm" variant="secondary" onClick={() => setForm('comp')}>Make complimentary</Button>}
          {b.source === 'complimentary' && <Button size="sm" variant="secondary" loading={busy && form === null}
            onClick={() => window.confirm('End complimentary access now? Ops will ask them to set up billing.') && run('opsEndComplimentary', {}, 'Complimentary access ended.')}>End complimentary</Button>}
          {!b.suspended
            ? <Button size="sm" variant="danger" onClick={() => setForm('suspend')}>Suspend</Button>
            : <Button size="sm" variant="secondary" onClick={() => setForm('reactivate')}>Reactivate</Button>}
        </div>
      </div>

      {b.suspended && (
        <Note tone="danger">Suspended {fmtDate(b.suspended.at)} by {b.suspended.by}: {b.suspended.reason}. Users see an "Account suspended" screen in Ops.</Note>
      )}
      {b.complimentary && (
        <Note tone="info">Complimentary: {b.complimentary.note}. {b.complimentary.until ? `Ends ${fmtDate(b.complimentary.until)}, after which Ops asks them to set up billing.` : 'No end date.'}</Note>
      )}
      {setup && !setup.completedAt && !liveStripe && (
        <Note tone="warning">
          Billing link sent to {setup.email} on {fmtDateTime(setup.createdAt)}
          {Date.parse(setup.expiresAt) < Date.now() ? ' (expired, send a new one).' : `, valid until ${fmtDateTime(setup.expiresAt)}.`}
        </Note>
      )}
      {error && <Note tone="danger">{error}</Note>}
      {notice && <Note tone="success">{notice}</Note>}

      {form === 'billing' && (
        <Form title="Set up billing" busy={busy} onCancel={() => setForm(null)} submitLabel="Email billing link"
          fields={[{ key: 'email', label: 'Send the Stripe link to', initial: adminEmail, type: 'email' }]}
          help={`£50/month including 10 machines, plus £5/month per extra machine (${c.machineCount} active machines now). They enter payment details on Stripe; access continues as it is until they do. Once they have paid, the organisation's admins get a confirmation email.`}
          onSubmit={v => run('opsStartBilling', { email: v.email }, `Billing link emailed to ${v.email}.`)} />
      )}
      {form === 'comp' && (
        <Form title="Make complimentary" busy={busy} onCancel={() => setForm(null)} submitLabel="Make complimentary"
          help="The organisation's admins get an email saying access is complimentary, with the end date if you set one. Your note stays internal."
          fields={[
            { key: 'note', label: 'Why (recorded)', initial: '', placeholder: 'e.g. Pilot partner until harvest' },
            { key: 'until', label: 'End date (optional)', initial: '', type: 'date' },
          ]}
          onSubmit={v => run('opsSetComplimentary', { note: v.note, until: v.until ? `${v.until}T23:59:00` : null }, (r) => r.emailed?.length ? `Account is now complimentary. Emailed ${r.emailed.join(', ')}.` : 'Account is now complimentary. No admin email was found, so no one was emailed.')} />
      )}
      {form === 'suspend' && (
        <Form title="Suspend access" busy={busy} onCancel={() => setForm(null)} submitLabel="Suspend" danger
          fields={[{ key: 'reason', label: 'Reason (recorded)', initial: '', placeholder: 'e.g. Unpaid invoices since June' }]}
          help="Everyone in this organisation is signed out and shown an Account suspended screen until you reactivate. Their data is untouched."
          onSubmit={v => run('opsSuspend', { reason: v.reason }, 'Organisation suspended.')} />
      )}
      {form === 'reactivate' && (
        <Form title="Reactivate access" busy={busy} onCancel={() => setForm(null)} submitLabel="Reactivate"
          fields={[{ key: 'note', label: 'Note (optional)', initial: '' }]}
          onSubmit={v => run('opsReactivate', { note: v.note }, 'Organisation reactivated.')} />
      )}

      <div style={{ marginTop: 16, borderTop: '0.5px solid var(--color-border-light)', paddingTop: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>History</div>
        {!log ? <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Loading...</div>
          : log.actions.length === 0 ? <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>No actions from HQ yet.</div>
          : log.actions.slice(0, 12).map(a => (
            <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 170px', gap: 12, fontSize: 12, padding: '5px 0' }}>
              <div style={{ color: 'var(--color-text-tertiary)' }}>{fmtDateTime(a.at)}</div>
              <div>{ACTION_LABELS[a.action] || a.action}{describe(a)}</div>
              <div style={{ color: 'var(--color-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.by}</div>
            </div>
          ))}
      </div>
    </Card>
  );
}

function describe(a) {
  const d = a.details || {};
  if (d.reason) return `: ${d.reason}`;
  if (d.note) return `: ${d.note}${d.emailed?.length ? ` (emailed ${d.emailed.join(', ')})` : ''}`;
  if (a.action === 'user_role_changed') return `: ${d.email} ${d.from || '?'} to ${d.to}`;
  if (d.email) return `: ${d.email}`;
  if (d.emailed?.length) return ` (emailed ${d.emailed.join(', ')})`;
  if (d.automatic) return ' (end date reached)';
  return '';
}

// Per-user actions, rendered in the Users table.
export function UserActions({ orgId, user, adminCount, onChanged, onError }) {
  const [busy, setBusy] = useState(false);
  const act = async (fn, data, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try { await callFn(fn, { orgId, uid: user.uid, ...data }); onChanged(); } catch (e) { onError(e.message); }
    setBusy(false);
  };
  const lastAdmin = user.role === 'org_admin' && adminCount <= 1;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
      <select value={user.role || ''} disabled={busy || lastAdmin || !user.hasLogin} title={lastAdmin ? 'Only admin; make someone else admin first' : undefined}
        onChange={e => act('opsSetUserRole', { role: e.target.value }, `Change ${user.name || user.email} to ${e.target.value.replace('org_', '')}?`)}
        style={{ ...inputStyle, width: 100, height: 26, fontSize: 11, padding: '0 6px' }}>
        <option value="org_admin">Admin</option>
        <option value="manager">Manager</option>
        <option value="operator">Operator</option>
      </select>
      {user.hasLogin && (user.disabled
        ? <Button size="sm" variant="secondary" loading={busy} onClick={() => act('opsSetUserDisabled', { disabled: false })}>Enable</Button>
        : <Button size="sm" variant="ghost" loading={busy} disabled={lastAdmin}
            onClick={() => act('opsSetUserDisabled', { disabled: true }, `Disable ${user.email}'s login? They are signed out straight away.`)}>Disable</Button>)}
      {user.hasLogin && !user.disabled && (
        <Button size="sm" variant="ghost" loading={busy}
          onClick={() => act('opsSendPasswordSetup', {}, `Email ${user.email} a link to set their password?`)}>Password email</Button>
      )}
    </div>
  );
}

function Form({ title, fields, help, submitLabel, danger, busy, onSubmit, onCancel }) {
  const [v, setV] = useState(Object.fromEntries(fields.map(f => [f.key, f.initial])));
  return (
    <div style={{ marginTop: 14, padding: 14, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ fontSize: 13, marginBottom: 10 }}>{title}</div>
      {help && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 10, lineHeight: 1.5 }}>{help}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: fields.length > 1 ? '2fr 1fr' : '1fr', gap: 10, maxWidth: 620 }}>
        {fields.map(f => (
          <div key={f.key}>
            <label style={labelStyle}>{f.label}</label>
            <input style={inputStyle} type={f.type || 'text'} value={v[f.key]} placeholder={f.placeholder}
              onChange={e => setV({ ...v, [f.key]: e.target.value })} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button size="sm" variant={danger ? 'danger' : 'primary'} loading={busy} onClick={() => onSubmit(v)}>{submitLabel}</Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function Pill({ tone, children }) {
  return <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: `var(--color-${tone}-light)`, color: `var(--color-${tone})`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{children}</span>;
}

function Note({ tone, children }) {
  return <div style={{ marginTop: 12, padding: '8px 12px', background: `var(--color-${tone}-light)`, color: `var(--color-${tone})`, borderRadius: 'var(--radius-md)', fontSize: 12, lineHeight: 1.5 }}>{children}</div>;
}
